import { Injectable, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { THREEx } from '@ar-js-org/ar.js-threejs'
import { ArToolkitContext } from '@ar-js-org/ar.js-threejs/types/ArToolkitContext';
import { ArToolkitSource } from '@ar-js-org/ar.js-threejs/types/ArToolkitSource';
import { Scene, WebGLRenderer } from 'three';
import { NgtCameraManual } from 'angular-three';

/**
 * AR initialization options
 * Matches AR.js ArToolkitContext and ArMarkerControls options
 */
export interface ArOptions {
  patternUrl?: string;        // ArMarkerControls - URL to pattern file
  patternRatio?: number;      // ArToolkitContext - Border/pattern ratio (0.5-0.95)
  size?: number;              // ArMarkerControls - Marker size in meters (larger = smaller scene)
  sceneScale?: number;        // Three.js Scene - Uniform scale multiplier (0.01-1, typically 0.05)
  smoothCount?: number;       // ArMarkerControls - Number of frames to smooth (1-30)
  smoothTolerance?: number;   // ArMarkerControls - Position tolerance (0.001-0.1)
  smoothThreshold?: number;   // ArMarkerControls - Threshold for smoothing (0-10)
}

/**
 * Device configuration for AR
 */
export interface ArDeviceConfig {
  isDesktop: boolean;
  browser: 'Firefox' | 'Chrome' | 'Safari' | 'Edge' | 'Unknown';
  containerElement?: HTMLElement; // Optional: container for AR video (Portal mode)
}

/**
 * Callbacks for AR lifecycle events
 * Decouples ArService from app-specific state management
 */
export interface ArCallbacks {
  /** Called when AR starts loading */
  onLoadingStart?: () => void;
  /** Called when AR finishes loading */
  onLoadingEnd?: () => void;
  /** Called when scene should be visible (marker detected) */
  onSceneVisible?: (visible: boolean) => void;
  /** Called when camera orientation is unsupported */
  onUnsupportedOrientation?: (unsupported: boolean) => void;
  /** Called when AR camera is enabled */
  onCameraEnabled?: (enabled: boolean) => void;
}

/**
 * AR bypass configuration
 * Replaces direct AppStore dependency
 */
export interface ArBypassConfig {
  /** Force bypass AR (for testing) */
  forceBypass?: boolean;
  /** Current campaign short code (for testing campaign detection) */
  campaignShortCode?: string;
  /** Enable development-only bypass checks */
  enableDevBypass?: boolean;
}

/**
 * AR Service - Shared AR.js integration for all apps
 *
 * **Phase E Refactor**: Fully decoupled from app-specific dependencies
 *
 * Provides AR marker detection and camera feed management using AR.js library.
 * Originally in libs/experience (app-specific) but moved to libs/shared to enable
 * AR preview in Portal and follow proper architecture (AR is rendering mode).
 *
 * **Architecture**:
 * - ✅ No UIService dependency (uses standard DOCUMENT injection)
 * - ✅ No AppStore dependency (uses callbacks for state updates)
 * - ✅ Configuration-based bypass logic (no direct store access)
 * - ✅ Optional device detection (passed via config)
 * - ✅ Fully standalone and reusable
 *
 * **Usage**:
 * ```typescript
 * const arService = inject(ArService);
 * const canvasStore = gameEngine.getCanvasStore();
 *
 * // Set callbacks for app-specific concerns
 * arService.setCallbacks({
 *   onLoadingStart: () => appStore.startLoading(),
 *   onLoadingEnd: () => appStore.stopLoading(),
 *   onSceneVisible: (visible) => updateUI(visible),
 *   onCameraEnabled: (enabled) => setCameraState(enabled)
 * });
 *
 * // Configure device info
 * arService.setDeviceConfig({
 *   isDesktop: deviceService.isDesktop(),
 *   browser: deviceService.getDeviceInfo().browser
 * });
 *
 * // Initialize AR
 * arService.initAR(
 *   canvasStore.scene,
 *   canvasStore.camera,
 *   canvasStore.gl,
 *   {
 *     patternUrl: 'path/to/pattern.patt',
 *     patternRatio: 0.75,
 *     smoothCount: 5,
 *     smoothTolerance: 0.01,
 *     smoothThreshold: 2
 *   }
 * );
 * ```
 *
 * @see Phase E: ArService migration to libs/shared
 * @see COMMIT_DEC_16_2025_PHASE_D5_TESTING.md
 */
@Injectable({
  providedIn: 'root'
})
export class ArService {
  // Standard Angular dependencies
  private document = inject(DOCUMENT);
  private window: Window & typeof globalThis;

  // AR.js components
  protected arToolkitSource?: ArToolkitSource;
  protected arToolkitContext?: ArToolkitContext;
  protected camera?: NgtCameraManual;
  protected renderer?: WebGLRenderer;
  protected scene?: Scene;
  protected stream?: MediaStream;
  protected track?: MediaStreamTrack;

  // Configuration (set by consuming app)
  private callbacks: ArCallbacks = {};
  private deviceConfig: ArDeviceConfig = { isDesktop: false, browser: 'Unknown' };
  private bypassConfig: ArBypassConfig = {};

  // Internal state
  keepVisible = false;

  // Marker detection state tracking (for debug logging)
  private previousMarkerVisible: boolean | null | undefined = null;

  constructor() {
    this.window = this.document.defaultView as Window & typeof globalThis;
  }

  /**
   * Set callbacks for AR lifecycle events
   * Decouples ArService from app-specific state management
   */
  setCallbacks(callbacks: ArCallbacks): void {
    this.callbacks = { ...this.callbacks, ...callbacks };
  }

  /**
   * Set device configuration for AR
   * Optional - defaults to non-desktop, unknown browser
   * @param config.containerElement - Optional container for AR video in Portal preview mode
   */
  setDeviceConfig(config: ArDeviceConfig): void {
    this.deviceConfig = config;
  }

  /**
   * Set bypass configuration
   * Optional - for testing and development
   */
  setBypassConfig(config: ArBypassConfig): void {
    this.bypassConfig = { ...this.bypassConfig, ...config };
  }

  /**
   * Wait for video element to be ready for AR tracking
   * Uses event-driven approach instead of arbitrary timeout
   * @returns Promise that resolves when video is playing, rejects after 5 seconds
   */
  private waitForVideoReady(video: HTMLVideoElement): Promise<void> {
    return new Promise((resolve, reject) => {
      // If video is already playing, resolve immediately
      if (video.readyState >= 3 && !video.paused) {
        console.log('[ArService] Video already ready');
        resolve();
        return;
      }

      let resolved = false;
      const timeout = setTimeout(() => {
        if (!resolved) {
          resolved = true;
          reject(new Error('Video ready timeout after 5 seconds'));
        }
      }, 5000);

      // Listen for playing event
      const onPlaying = () => {
        if (!resolved) {
          resolved = true;
          clearTimeout(timeout);
          video.removeEventListener('playing', onPlaying);
          console.log('[ArService] Video playing event received');
          resolve();
        }
      };

      // Listen for canplay event as backup
      const onCanPlay = () => {
        if (!resolved && video.readyState >= 3) {
          resolved = true;
          clearTimeout(timeout);
          video.removeEventListener('canplay', onCanPlay);
          video.removeEventListener('playing', onPlaying);
          console.log('[ArService] Video canplay event received');
          resolve();
        }
      };

      video.addEventListener('playing', onPlaying);
      video.addEventListener('canplay', onCanPlay);
    });
  }

  /**
   * Determines if AR should be bypassed based on multiple intelligent conditions
   */
  get shouldBypassAR(): boolean {
    // Check force bypass from config
    if (this.bypassConfig.forceBypass) {
      return true;
    }

    // First check if URL explicitly disables bypass
    if (this.hasExplicitBypassDisabled()) {
      console.log('🔧 AR bypass explicitly disabled via URL parameter');
      return false;
    }

    const hasBypassFlag = this.hasARBypassFlag();
    const isTestingCampaign = this.isTestingCampaign();
    const hasTestingUA = this.hasTestingUserAgent();

    const result = hasBypassFlag || isTestingCampaign || hasTestingUA;

    return result;
  }

  /**
   * Check if we're in development environment
   */
  private isDevelopmentEnvironment(): boolean {
    // Check for development indicators
    const isLocalhost = this.window.location.hostname === 'localhost' ||
                       this.window.location.hostname === '127.0.0.1';
    const hasDevPort = ['3000', '4200', '5173', '8080'].includes(this.window.location.port);
    const hasDebugParam = this.window.location.search.includes('debug=true');

    return isLocalhost || hasDevPort || hasDebugParam;
  }

  /**
   * Check if URL explicitly disables bypass (overrides all other bypass methods)
   */
  private hasExplicitBypassDisabled(): boolean {
    const urlParams = new URLSearchParams(this.window.location.search);
    const explicitDisable = urlParams.get('arBypass') === 'false';

    // If explicitly disabled via URL, clear localStorage flag
    if (explicitDisable) {
      try {
        this.window.localStorage.removeItem('arBypass');
      } catch (e) {
        console.warn('Failed to remove arBypass from localStorage:', e);
      }
    }

    return explicitDisable;
  }

  /**
   * Check for explicit AR bypass URL parameter
   * SECURITY: Only works in development environment
   * SIMPLIFIED: Now only accepts arBypass=true for clarity
   */
  private hasARBypassFlag(): boolean {
    // Security check - URL bypass only works in development
    if (!this.bypassConfig.enableDevBypass && !this.isDevelopmentEnvironment()) {
      return false;
    }

    // Check URL params first
    const urlParams = new URLSearchParams(this.window.location.search);
    const urlBypass = urlParams.get('arBypass') === 'true';

    // If found in URL, store in localStorage for persistence after query param clearing
    if (urlBypass) {
      try {
        this.window.localStorage.setItem('arBypass', 'true');
      } catch (e) {
        console.warn('Failed to store arBypass in localStorage:', e);
      }
      return true;
    }

    // Check localStorage for persisted bypass flag
    try {
      return this.window.localStorage.getItem('arBypass') === 'true';
    } catch (e) {
      console.warn('Failed to read arBypass from localStorage:', e);
      return false;
    }
  }

  /**
   * Check if current campaign is a testing/demo campaign
   * SECURITY: Only works in development environment
   */
  private isTestingCampaign(): boolean {
    // Security check - testing campaign bypass only works in development
    if (!this.bypassConfig.enableDevBypass && !this.isDevelopmentEnvironment()) {
      return false;
    }

    const short = this.bypassConfig.campaignShortCode;
    if (!short) return false;

    // Testing patterns
    const testingPatterns = [
      /^test-/,     // test-*
      /^demo-/,     // demo-*
      /^dev-/,      // dev-*
      /^local-/,    // local-*
      /^staging-/,  // staging-*
      /^default$/,  // our current default testing campaign
    ];

    return testingPatterns.some(pattern => pattern.test(short));
  }

  /**
   * Check for automated testing user agents (Cypress, Puppeteer, etc.)
   * SECURITY: Only works in development environment
   */
  private hasTestingUserAgent(): boolean {
    // Security check - user agent bypass only works in development
    if (!this.bypassConfig.enableDevBypass && !this.isDevelopmentEnvironment()) {
      return false;
    }

    const userAgent = this.window.navigator.userAgent.toLowerCase();
    const testingAgents = ['cypress', 'puppeteer', 'playwright', 'selenium', 'headless'];

    return testingAgents.some(agent => userAgent.includes(agent));
  }

  /**
   * Force AR bypass for programmatic testing
   * Useful for E2E tests that need to control AR behavior
   */
  forceARBypass(bypass = true): void {
    this.keepVisible = bypass;
    if (bypass) {
      console.log('🔧 AR bypass forced programmatically');
    }
  }

  /**
   * Check if AR is currently being bypassed
   */
  get isARBypassed(): boolean {
    return this.shouldBypassAR || this.keepVisible;
  }

  /**
   * Debug method to show current AR bypass status
   */
  logARStatus(): void {
    console.log('🔧 AR Status:', {
      shouldBypassAR: this.shouldBypassAR,
      keepVisible: this.keepVisible,
      isARBypassed: this.isARBypassed
    });
  }

  initAR(scene: Scene, camera: NgtCameraManual, renderer: WebGLRenderer, options?: ArOptions ): void {
    console.log('[ArService] initAR() called');

    // Check if we already have AR initialized - if so, just resume it
    const existingVideo = this.document.getElementById('arjs-video') as HTMLVideoElement;
    if (existingVideo && this.arToolkitSource && this.arToolkitContext) {
      console.log('[ArService] Reusing existing AR initialization');
      this.resumeExistingAR(scene, camera, renderer, options);
      return;
    }

    // First time initialization
    this.scene = scene;
    this.camera = camera;
    this.renderer = renderer;

    console.log('[ArService] Initializing AR.js with auto-detection');

    // Initialize AR.js source with MINIMAL configuration
    // Let AR.js handle resolution, orientation, and camera constraints automatically
    // This follows AR.js-threejs best practices and fixes aspect ratio issues
    this.arToolkitSource = new THREEx.ArToolkitSource({
      sourceType: 'webcam',
      // Use high 'ideal' values - AR.js will use whatever the device camera provides
      // These are suggestions, not hard constraints (camera provides natural resolution)
      sourceWidth: this.window.innerWidth > this.window.innerHeight ? 640 : 480,
      sourceHeight: this.window.innerWidth > this.window.innerHeight ? 480 : 640,
    });

    this.arToolkitSource.init(() => {
      console.log('[ArService] Camera initialized with natural resolution');

      // Apply container-specific positioning if provided
      this.applyVideoPositioning();

      // Wait for video to be ready, then trigger callbacks
      this.onCameraReady();
    }, (err: unknown) => {
      console.error('[ArService] Camera initialization failed:', err);
      this.callbacks.onLoadingEnd?.();
    });

    // Initialize AR.js context with marker detection settings
    // AR.js automatically detects orientation from video dimensions
    this.arToolkitContext = new THREEx.ArToolkitContext({
      cameraParametersUrl: 'assets/camera_para.dat',
      detectionMode: 'mono',
      patternRatio: options?.patternRatio || 0.75,
      // AR.js will auto-detect orientation - no manual intervention needed
    });

    this.arToolkitContext.init(() => {
      console.log('[ArService] AR context initialized with options:', options);
      if (this.arToolkitContext && this.camera) {
        // Copy projection matrix from AR.js (handles aspect ratio correctly)
        this.camera.projectionMatrix.copy(this.arToolkitContext.getProjectionMatrix());

        this.arToolkitContext.arController.orientation = 'landscape';
        this.arToolkitContext.arController.options.orientation = 'landscape';

        // Apply scene scale if specified
        if (options?.sceneScale !== undefined && this.scene) {
          this.scene.scale.setScalar(options.sceneScale);
          console.log('[ArService] Applied scene scale:', options.sceneScale);
        }

        // Initialize marker tracking with smoothing parameters
        new THREEx.ArMarkerControls(this.arToolkitContext, this.camera, {
          type: 'pattern',
          patternUrl: options?.patternUrl || 'assets/patt.hiro',
          changeMatrixMode: 'cameraTransformMatrix',
          size: options?.size || 1,
          smooth: true,
          smoothCount: options?.smoothCount || 5,
          smoothTolerance: options?.smoothTolerance || 0.01,
          smoothThreshold: options?.smoothThreshold || 2
        });

        this.callbacks.onLoadingEnd?.();
      }
    });

    // Handle window resize (updates projection matrix when viewport changes)
    addEventListener('resize', () => {
      this.onWindowResize();
    });
  }

  /**
   * Helper method for camera ready state
   * Called after camera is initialized to ensure video is playing
   */
  private onCameraReady(): void {
    const video = this.document.getElementById('arjs-video') as HTMLVideoElement;
    if (!video) return;

    // Wait for video metadata to load before proceeding
    this.waitForVideoReady(video).then(() => {
      this.onWindowResize(); // Update projection matrix
      this.addCSSClass('camera-enabled');
      this.callbacks.onCameraEnabled?.(true);
      this.callbacks.onLoadingEnd?.();
    }).catch((err) => {
      console.warn('[ArService] Video ready timeout, proceeding anyway:', err);
      this.onWindowResize();
      this.addCSSClass('camera-enabled');
      this.callbacks.onCameraEnabled?.(true);
      this.callbacks.onLoadingEnd?.();
    });
  }

  /**
   * Resume existing AR initialization when component remounts
   * Reuses existing video stream and AR context
   */
  private resumeExistingAR(scene: Scene, camera: NgtCameraManual, renderer: WebGLRenderer, options?: ArOptions): void {
    this.scene = scene;
    this.camera = camera;
    this.renderer = renderer;

    // Update projection matrix from existing AR context
    if (this.camera && this.arToolkitContext) {
      this.camera.projectionMatrix.copy(this.arToolkitContext.getProjectionMatrix());
    }

    // Apply scene scale
    if (options?.sceneScale !== undefined && this.scene) {
      this.scene.scale.setScalar(options.sceneScale);
    }

    // Resume video playback if paused
    const existingVideo = this.document.getElementById('arjs-video') as HTMLVideoElement;
    if (existingVideo?.paused) {
      existingVideo.play();
    }

    existingVideo.style.display = 'block';
    this.addCSSClass('camera-enabled');
    this.callbacks.onCameraEnabled?.(true);
    this.callbacks.onLoadingEnd?.();
  }

  /**
   * Apply AR bypass logic globally (for use outside of AR context)
   * This allows AR bypass to work on any page, not just when AR is active
   */
  applyGlobalBypass(): void {
    if (this.shouldBypassAR) {
      console.log('🔧 GLOBAL AR BYPASS ACTIVE - Adding force-scene-visible class');
      this.addCSSClass('force-scene-visible');
      this.removeCSSClass('scene-visible');
      this.callbacks.onSceneVisible?.(true);

      if (this.hasARBypassFlag()) {
        console.log('🔧 AR bypassed: URL parameter detected', this.window.location.search);
      } else if (this.isTestingCampaign()) {
        console.log('🔧 AR bypassed: Testing campaign pattern detected');
      } else if (this.hasTestingUserAgent()) {
        console.log('🔧 AR bypassed: Automated testing user agent detected');
      }
    } else {
      console.log('🔧 GLOBAL AR BYPASS INACTIVE - Removing force-scene-visible class');
      this.removeCSSClass('force-scene-visible');
      this.callbacks.onSceneVisible?.(false);
    }
  }

  injectBeforeRender(): void {
    // Update AR tracking (this must be called every frame)
    this.arToolkitContext?.update(this.arToolkitSource?.domElement);

    if (this.shouldBypassAR) {
      this.addCSSClass('force-scene-visible');
      this.removeCSSClass('scene-visible');
      this.callbacks.onSceneVisible?.(true);
    } else {
      this.removeCSSClass('force-scene-visible');
      const isVisible = this.keepVisible || this.camera?.visible;

      // Debug: Log marker detection state changes only
      if (this.previousMarkerVisible !== isVisible) {
        if (isVisible) {
          console.log('✅ [ArService] Marker DETECTED - Scene visible');
          console.log('   Camera visible:', this.camera?.visible);
          console.log('   Keep visible:', this.keepVisible);
        } else {
          console.log('❌ [ArService] Marker LOST - Scene hidden');
        }
        this.previousMarkerVisible = isVisible;
      }

      if (isVisible) {
        this.addCSSClass('scene-visible');
        this.callbacks.onSceneVisible?.(true);
      } else {
        this.removeCSSClass('scene-visible');
        this.callbacks.onSceneVisible?.(false);
      }
    }
  }

  private onWindowResize(): void {
    if (!this.arToolkitContext) return;

    const unsupported = !this.deviceConfig.isDesktop &&
                       (this.window.innerWidth > (this.window.innerHeight * 1.25));

    if (unsupported) {
      this.addCSSClass('unsupported-orientation');
      this.callbacks.onUnsupportedOrientation?.(true);
    } else {
      this.removeCSSClass('unsupported-orientation');
      this.callbacks.onUnsupportedOrientation?.(false);
    }

    if (this.arToolkitSource) {
      this.arToolkitSource.onResizeElement();

      if (this.arToolkitContext.arController !== null) {
        this.arToolkitSource.copyElementSizeTo(this.arToolkitContext.arController.canvas);
      }
    }

    this.camera?.updateProjectionMatrix();
  }

  /**
   * Pause AR tracking without destroying resources
   * Video element and AR context stay alive for fast resume
   */
  pause(): void {
    console.log('[ArService] Pausing AR (keeping resources alive)');

    const video = this.document.getElementById('arjs-video') as HTMLVideoElement;
    if (video) {
      video.pause();
      video.style.display = 'none'; // Hide but don't remove
    }

    this.removeCSSClass('camera-enabled');
    this.callbacks.onCameraEnabled?.(false);
  }

  /**
   * Completely destroy AR resources
   * Only call this when component is being destroyed, not when toggling AR off
   */
  destroy(): void {
    console.log('[ArService] Destroying AR resources');

    this.renderer?.dispose();
    this.scene?.clear();
    this.camera = undefined;
    this.scene = undefined;
    this.renderer = undefined;
    this.keepVisible = false;

    // Stop the camera stream
    this.stream?.getTracks().map(track => track.stop());
    this.stream = undefined;
    this.track = undefined;

    // Remove video element
    this.removeElementById('arjs-video');
    this.arToolkitSource = undefined;
    this.removeCSSClass('camera-enabled');
    this.callbacks.onCameraEnabled?.(false);

    // Dispose AR Context
    if (this.arToolkitContext?.arController?.cameraParam?.dispose) {
      this.arToolkitContext.arController.cameraParam.dispose();
    }
    if (this.arToolkitContext?.arController?.dispose) {
      this.arToolkitContext.arController.dispose();
    }
    this.arToolkitContext = undefined;
  }

  // ============================================================================
  // Private Helper Methods
  // ============================================================================

  /**
   * Apply video positioning to container
   * Both Portal and Experience apps provide explicit containers for AR video
   * Video is positioned absolutely within the provided container
   */
  private applyVideoPositioning(): void {
    const video = this.document.getElementById('arjs-video') as HTMLVideoElement;
    if (!video) return;

    const container = this.deviceConfig.containerElement;

    if (container) {
      // Verify container is valid and in DOM
      if (container.parentElement && this.document.contains(container)) {
        // Position video absolutely within container
        video.style.position = 'absolute';
        video.style.left = '0';
        video.style.top = '0';
        video.style.width = '100%';
        video.style.height = '100%';
        video.style.objectFit = 'cover';
        video.style.zIndex = '-1'; // Behind 3D canvas

        // Move video into container
        container.style.position = 'relative';
        container.appendChild(video);
      } else {
        console.warn('[ArService] Container provided but not valid, using default positioning');
        this.applyDefaultPositioning(video);
      }
    } else {
      // No container provided - use default positioning
      this.applyDefaultPositioning(video);
    }
  }

  /**
   * Apply default video positioning when no container provided
   * Full screen fixed positioning, no DOM manipulation
   */
  private applyDefaultPositioning(video: HTMLVideoElement): void {
    // Don't move video element - leave it where AR.js created it
    // Just apply full-screen fixed positioning
    video.style.position = 'fixed';
    video.style.left = '0';
    video.style.top = '0';
    video.style.width = '100vw';
    video.style.height = '100vh';
    video.style.objectFit = 'cover';
    video.style.zIndex = '-999'; // Behind all UI
  }

  /**
   * Add CSS class to document element
   */
  private addCSSClass(className: string): void {
    if (className && !this.document.documentElement.classList.contains(className)) {
      this.document.documentElement.classList.add(className);
    }
  }

  /**
   * Remove CSS class from document element
   */
  private removeCSSClass(className: string): void {
    if (className) {
      this.document.documentElement.classList.remove(className);
    }
  }

  /**
   * Remove element from DOM by ID
   */
  private removeElementById(id: string): void {
    this.document.getElementById(id)?.remove();
  }
}
